import React, { useCallback, useEffect, useMemo, useState } from 'react'
import produce from 'immer'
import { toast } from 'react-toastify'
import Popup from 'react-popup'
import Modal from 'src/components/app/customModal/Modal'
import Input from 'src/components/base/Input'
import Checkbox from 'src/components/base/Checkbox'
import Button from 'src/components/base/Button'
import ColorField from 'src/components/configuration/editor/defaultFields/colorField/ColorField'
import MultiSelectInput from 'src/components/base/MultiSelectInput'
import { Product } from 'src/types'
import useExtractProductFromConfig from 'src/hooks/useExtractProductFromConfig'
import useCapabilityToChangeUnitOnAllProducts from 'src/hooks/useCapabilityToChangeUnitOnAllProducts'

import { useConfig } from '../ConfigContext'
import ManageProductSection from './ManageProductSection'
import { getAnyProductExistWithDifferentUnit, getProductErrors } from '../utils'

import './ProductHolderModal.scss'

export type BaseProductHolder = {
  name?: string
  color?: string
  reverseStartPlacement?: boolean
  bollardDefaultStart?: number
  bollardDefaultEnd?: number
  products: Product[]
}

export type ProductHolderModalProps<T extends BaseProductHolder> = {
  selected: T | null
  isOpen: boolean
  onConfirm: (entity: T | undefined) => void
  onCancel?: () => void
  subjectName: string
  idPrefix: string
  validateEntity: (entity: T) => string[]
  renderTopFields: (ctx: {
    entity: T
    setEntity: (recipe: (draft: T) => void) => void
    idPrefix: string
  }) => React.ReactNode
}

function ProductHolderModal<T extends BaseProductHolder>(props: ProductHolderModalProps<T>) {
  const { selected, onConfirm, isOpen, onCancel, subjectName, idPrefix, validateEntity, renderTopFields } = props

  const [entity, setEntity] = useState<T | null>(selected)
  const [inputSelectedProductGroup, setInputSelectedProductGroup] = useState<string[]>([])
  const [productGroupErrorMessage, setProductGroupErrorMessage] = useState<string>('')
  const { config, setConfig } = useConfig()
  const { changeUnitOnAllProducts } = useCapabilityToChangeUnitOnAllProducts({ config, setConfig })
  const { uniqueProducts, productGroupMap, getHolderNamesBasedOnProductNameAndGroup } = useExtractProductFromConfig(
    config
  )
  const isEdit = !!selected?.name
  const title = isEdit ? `Edit ${subjectName}` : `Add ${subjectName}`

  // Combine the product group from the config and the input selected product group
  const productGroupOptions = useMemo(() => {
    return [...Object.keys(productGroupMap), ...inputSelectedProductGroup].filter((v, i, a) => a.indexOf(v) === i)
  }, [inputSelectedProductGroup, productGroupMap])

  const selectedProductGroup = useMemo(() => {
    const holder = (entity?.products || []).map(p => p.productGroup).filter((v, i, a) => a.indexOf(v) === i)
    return [...holder, ...inputSelectedProductGroup].filter((v, i, a) => a.indexOf(v) === i)
  }, [inputSelectedProductGroup, entity?.products])

  const setEntityDraft = (recipe: (draft: T) => void) => {
    setEntity(prev => {
      if (!prev) return prev as T | null
      return produce(prev as T, (draft: T) => {
        recipe(draft)
      })
    })
  }

  const setProducts = (recipe: (draft: Product[]) => void) => {
    setEntity(prev => {
      if (!prev) return prev as T | null
      return produce(prev as T, (draft: T) => {
        recipe(draft.products)
      })
    })
  }

  const errorCheckingHasError = useCallback(() => {
    if (!entity) return true
    const errors = validateEntity(entity)
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error))
      return true
    }
    const productErrors = (entity.products || [])
      .map((product, productIndex) => {
        const errors = getProductErrors(product)
        if (errors.length > 0) {
          errors.forEach(error => toast.error(`${product.product || `Product ${productIndex + 1}`}: ${error}`))
          return true
        }
        return false
      })
      .some(error => error)
    if (productErrors) {
      return true
    }
    return false
  }, [entity, validateEntity])

  const onSelectedOptionsProductGroupChange = useCallback(
    (newSelectedOptions: string[]) => {
      // Check if the removed product group is contained products and show error message if so
      const removedOptions = selectedProductGroup.filter(option => !newSelectedOptions.includes(option))
      const preSelectedOptions = selectedProductGroup.filter(option => !inputSelectedProductGroup.includes(option))
      if (removedOptions.length > 0) {
        const isRemovedOptionsIsContainedInPreSelectedOptions = removedOptions.some(option =>
          preSelectedOptions.includes(option)
        )
        if (isRemovedOptionsIsContainedInPreSelectedOptions) {
          setProductGroupErrorMessage('Remove all products before deleting the product group.')
        }
      }

      setInputSelectedProductGroup(newSelectedOptions.filter(option => !preSelectedOptions.includes(option)))
    },
    [inputSelectedProductGroup, selectedProductGroup]
  )

  const onConfirmInternal = useCallback(() => {
    if (!entity) return
    const productWithDifferentUnit = getAnyProductExistWithDifferentUnit(uniqueProducts, entity.products)
    const isThereAnyProductExistWithDifferentUnit = productWithDifferentUnit.length > 0

    if (isThereAnyProductExistWithDifferentUnit) {
      Popup.create({
        title: 'Not all units match',
        className: 'small-picker',
        content: (
          <div>
            <p>
              Products with the same name should have the same unit. Based on the latest changes, do you want to change
              the following product units?
            </p>
            <table className="table-product-unit-proposal">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Group</th>
                  <th>Jetty/Cargo belt</th>
                  <th>Unit</th>
                </tr>
              </thead>
              <tbody>
                {productWithDifferentUnit.map(p => {
                  const holderNames = getHolderNamesBasedOnProductNameAndGroup(p.product, p.productGroup)
                  return (
                    <tr key={p.product}>
                      <td>{p.product}</td>
                      <td>{p.productGroup}</td>
                      <td>
                        {holderNames.map((holder, index) => {
                          return (
                            <div key={index} className="holder-name">
                              {holder.name} ({holder.type})
                            </div>
                          )
                        })}
                      </td>
                      <td>
                        <s>{p.productUnit}</s> to{' '}
                        <b>
                          {
                            entity.products.find(up => up.product === p.product && up.productGroup === p.productGroup)
                              ?.productUnit
                          }
                        </b>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <button className={`btn valid delete-btn`} type="button" onClick={() => Popup.close()}>
              <span>Cancel</span>
            </button>
            <button
              className={`save-btn btn valid`}
              type="button"
              onClick={() => {
                onConfirm(entity)
                productWithDifferentUnit.forEach(p => {
                  const newUnit = entity.products.find(
                    up => up.product === p.product && up.productGroup === p.productGroup
                  )?.productUnit
                  changeUnitOnAllProducts(p.product, p.productGroup, newUnit)
                })
                Popup.close()
              }}
            >
              <span>Overwrite all units</span>
            </button>
          </div>
        )
      })
      return
    }
    if (errorCheckingHasError()) {
      return
    }
    onConfirm(entity)
  }, [
    entity,
    uniqueProducts,
    errorCheckingHasError,
    onConfirm,
    getHolderNamesBasedOnProductNameAndGroup,
    changeUnitOnAllProducts
  ])

  return (
    <Modal.Wrapper isOpen={isOpen} title={title} onCloseModal={onCancel}>
      <Modal.Content>
        <div className="tq-modal-content w-screen-lg">
          <form className="tq-form product-holder-modal-form">
            <div className="tq-form-group">
              <div className="name-color-container">
                <Input
                  label="Name"
                  id={`${idPrefix}-name`}
                  value={entity?.name ?? ''}
                  onChange={e => {
                    const newName = e.target.value
                    setEntityDraft(draft => {
                      draft.name = newName
                    })
                  }}
                  required
                  parentStyle={{ flex: 1 }}
                  flexibleWidth
                />
                <ColorField
                  color={entity?.color ? entity.color : '#000'}
                  onColorChange={color => {
                    setEntityDraft(draft => {
                      draft.color = color.hex
                    })
                  }}
                  style={{ height: '48px', width: '48px' }}
                />
              </div>
              {entity && renderTopFields({ entity, setEntity: setEntityDraft, idPrefix })}
            </div>
            <div className="tq-form-group align-bottom">
              <Checkbox
                label="Reverse product placement"
                id="reverse-product-placement"
                checked={entity?.reverseStartPlacement ?? false}
                onChange={e => {
                  const newReverseStartPlacement = e.target.checked
                  setEntityDraft(draft => {
                    draft.reverseStartPlacement = newReverseStartPlacement
                  })
                }}
                parentStyle={{ flex: 1 }}
              />
              <Input
                label="Default bollard start"
                id="default-bollard-start"
                type="number"
                value={entity?.bollardDefaultStart ?? ''}
                onChange={e => {
                  const newBollardDefaultStart = Number(e.target.value)
                  setEntityDraft(draft => {
                    draft.bollardDefaultStart = newBollardDefaultStart
                  })
                }}
                required
                parentStyle={{ flex: 1 }}
                flexibleWidth
              />
              <Input
                label="Default bollard end"
                id="default-bollard-end"
                type="number"
                value={entity?.bollardDefaultEnd ?? ''}
                onChange={e => {
                  const newBollardDefaultEnd = Number(e.target.value)
                  setEntityDraft(draft => {
                    draft.bollardDefaultEnd = newBollardDefaultEnd
                  })
                }}
                required
                parentStyle={{ flex: 1 }}
                flexibleWidth
              />
            </div>
            <div className="tq-form-group">
              <MultiSelectInput
                label="Product groups"
                options={productGroupOptions}
                selectedOptions={selectedProductGroup}
                onSelectedOptionsChange={onSelectedOptionsProductGroupChange}
                numberOfLabelToShow={4}
                enableCustomOption
                error={productGroupErrorMessage}
                disableSelectAllOptionButton
              />
            </div>
            <div className="tq-form-group">
              {entity && (
                <ManageProductSection
                  selectedProductGroup={selectedProductGroup}
                  products={entity.products}
                  setProducts={setProducts}
                  holderColor={entity?.color ?? ''}
                />
              )}
            </div>
            <div className="products-section-footer">
              <Button variant="neutral-tertiary" type="button" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="button" onClick={onConfirmInternal}>
                Confirm
              </Button>
            </div>
          </form>
        </div>
      </Modal.Content>
    </Modal.Wrapper>
  )
}

export default ProductHolderModal
